import { observable, action } from 'mobx';
import autobind from 'autobind-decorator';
import { RootStore } from '../store';
import { logger, RestaurantUtils } from '@lib/common';
import { connect, type StreamFeed } from 'getstream';
import cloneDeep from 'lodash/cloneDeep';
import Push, { PushNotificationParams } from 'push.js';
import { Howl, Howler } from 'howler';
import { config } from '../../config';
import { ExpiryCache } from '../../core/expiry_cache';
import { UI } from '../../core/ui';
import Ably, { Realtime } from 'ably';

const client = connect('6ue6uqqptjeq', null, '44988');

// INIT NOTIFICATION STUFF
(() => {
  try {
    Push.config({
      serviceWorker: '/serviceWorker.js',
    });
    Howler.autoSuspend = false;
    // @ts-ignore
    Howler.mobileAutoEnable = true;
    // @ts-ignore
    Howler.autoUnlock = true;
    new Howl({
      src: [`${config.urls.cloudfront}/assets/audio/job-done.mp3`],
      volume: 0,
    }).play();

    window.addEventListener('unhandledrejection', event => {
      if (event.reason?.message?.includes('Connection closed')) {
        console.log('Ably error:', event.reason.message);
        event.preventDefault();
      }
    });
  } catch (e) {
    logger.captureException(e);
  }
})();

export interface NotificationsState {
  items: T.Lib.GetStream.NotificationItem[];
  unread: number;
  unseen: number;
  active: boolean;
  loading: boolean;
  all_items_queried: boolean;
  audio_unlocked: boolean;
  modal_item: T.Lib.GetStream.NotificationItem | null;
}

const baseNotificationState = () => ({
  items: [],
  unread: 0,
  unseen: 0,
  active: false,
  loading: false,
  all_items_queried: false,
  audio_unlocked: Howler.ctx ? Howler.ctx.state === 'running' : false,
  modal_item: null,
});

@autobind
export class NotificationsStore {
  store: RootStore;
  feed?: StreamFeed;
  subscription?: any;
  pushNotificationsInitialized = false;
  soundPlaying = false;
  ablyOrg?: Realtime;
  ablyRestaurant?: Realtime;
  ablyOrder?: Realtime;
  @observable s: NotificationsState;
  @observable last_registered_device_id: string | null = null;
  menuEditingPageRefCount = 0;
  menuEditingClaimTimer?: ReturnType<typeof setTimeout>;
  menuEditStalenessInterval?: ReturnType<typeof setInterval>;
  ablyRestaurantRid: string | null = null;

  // Menu presence: who else has a menu screen open right now.
  menuPresenceRefCount = 0;
  menuPresenceMenuId: string | null = null;
  menuPresenceEntityType: MenuPresenceEntityType | null = null;
  menuPresenceEntityId: string | null = null;
  menuPresenceEntityName: string | null = null;
  menuPresenceActivity: 'viewing' | 'editing' = 'viewing';
  menuPresenceSince = 0;
  menuPresenceSyncTimer?: ReturnType<typeof setTimeout>;


  constructor(store: RootStore) {
    this.store = store;
    this.s = baseNotificationState();
    this.initPushNotification();
    this.anInit();
  }

  // ABLY ORGANISATION
  ablyOrgStart = (authToken: string, orgId: string) => {
    this.ablyOrgStop();

    const ably = new Ably.Realtime({
      authUrl: config.urls.api + '/dashboard/ably/organisation/token-request',
      authHeaders: {
        'Authorization-Dashboard': authToken,
      },
      recover: (_, cb) => {
        cb(true);
      },
    });

    this.ablyOrg = ably;

    const channel = ably.channels.get(`private:organisation:${orgId}`);

    channel.subscribe('restaurant:updated', this.ablyOrgHandleRestaurantUpdate);
    channel.subscribe('device:registered', this.ablyOrgHandleDeviceRegistered);
  };
  ablyOrgStop = () => {
    if (this.ablyOrg) {
      this.ablyOrg.close();
      delete this.ablyOrg;
    }
  };
  ablyOrgHandleRestaurantUpdate = (message: Ably.Message) => {
    try {
      const restaurant_id = message.data.restaurant_id;
      const update = message.data.update as Partial<T.Schema.Restaurant.RestaurantSchema>;
      this.store.updateRestaurantComplete(restaurant_id, update);
    } catch (e) {
      logger.captureException(e);
    }
  };

  ablyOrgHandleDeviceRegistered = (message: Ably.Message) => {
    try {
      this.last_registered_device_id = message.data.device_registration_id;
    } catch (e) {
      logger.captureException(e);
    }
  };

  // ABLY RESTAURANT
  ablyRestaurantInit(rid: string) {
    // DELAY BY 2 SECONDS TO PREVENT SIMULTANEOUS CONNECTION ISSUE
    setTimeout(() => {
      try {
        this.ablyRestaurantStop();
        this.store.updateAbly({
          status: 'disconnected',
          connected_once: false,
          printers: [],
        });

        const rne = this.store.restrictions._.restaurantNotificationsEnabled;
        if (!rne) {
          return;
        }

        const auth = this.store.auth;

        const ably = new Ably.Realtime({
          authUrl: config.urls.api + '/dashboard/ably/token-request',
          authHeaders: {
            'Authorization-Dashboard': `${auth.token}`,
            'Authorization-Restaurant': `${rid}`,
          },
          recover: (_, cb) => {
            cb(true);
          },
        });

        this.ablyRestaurant = ably;
        // Pin the channel to the restaurant this connection was opened for.
        // The field existed but was never assigned, so every caller fell back
        // to the currently selected restaurant — which is a different thing
        // while a restaurant switch is in flight.
        this.ablyRestaurantRid = rid;

        ably.connection.on(({ current }) => {
          if (current === 'initialized' || current === 'connecting') {
            this.store.updateAbly({ status: 'connecting' });
          } else if (current === 'connected') {
            this.store.updateAbly({
              status: current,
              connected_once: true,
            });
            // A reconnect drops presence, so re-announce if a menu screen is
            // still open. Without this the operator vanishes from everyone
            // else's roster after any network blip.
            if (this.menuPresenceRefCount > 0) {
              void this.menuPresencePublish();
            }
          } else {
            this.store.updateAbly({ status: 'disconnected' });
          }
        });

        const channel = ably.channels.get(`private:restaurant:${rid}`);

        channel.subscribe('order:created', this.ablyRestaurantHandleOrderNew);
        channel.subscribe('order:updated', this.ablyRestaurantHandleOrderUpdate);
        channel.subscribe('order_dish:updated', this.ablyHandleOrderDishUpdate);
        channel.subscribe('printer:job-update', this.ablyRestaurantHandlePrinterJobUpdate);
        channel.subscribe(
          'gift_card_purchase:pending_confirmation',
          this.ablyRestaurantHandleGiftCardPurchaseCreated,
        );
        channel.subscribe(
          'gift_card_purchase:confirmed',
          this.ablyRestaurantHandleGiftCardPurchaseCreated,
        );

        // UPDATE PRINTERS THAT ARE ONLINE
        channel.presence.subscribe('enter', member => {
          if (member.data && member.data.type === 'printer') {
            this.ablyRestaurantPrinterAdd(member.clientId);
          }
        });
        channel.presence.subscribe('leave', member => {
          this.ablyRestaurantPrinterRemove(member.clientId);
        });

        // Menu presence rides the same set. Any change rebuilds the roster from
        // the whole set rather than being applied incrementally.
        channel.presence.subscribe(['enter', 'update', 'leave'], () => {
          this.scheduleMenuPresenceRefresh();
        });

        const sync_printers = async () => {
          const members = await channel.presence.get();
          if (members.length) {
            for (const member of members) {
              if (member.data && member.data.type === 'printer') {
                this.ablyRestaurantPrinterAdd(member.clientId);
              }
            }
          }
        };

        sync_printers();

        setInterval(sync_printers, 120000);
      } catch (e) {
        console.log({ e });
        logger.captureException(e);
      }
    }, 2000);
  }

  ablyRestaurantStop = () => {
    if (this.ablyRestaurant) {
      this.ablyRestaurant.close();
      delete this.ablyRestaurant;
    }
    this.ablyRestaurantRid = null;
    this.store.updateAbly({ menuPresence: [] });
    if (this.menuPresenceSyncTimer) {
      clearTimeout(this.menuPresenceSyncTimer);
      delete this.menuPresenceSyncTimer;
    }
  };

  ablyRestaurantPrinterAdd = (id: string) => {
    const { store } = this;
    if (store.ably.printers.indexOf(id) === -1) {
      const printers = [...store.ably.printers];
      printers.push(id);
      store.updateAbly({ printers });
    }
  };

  ablyRestaurantPrinterRemove = (id: string) => {
    const { store } = this;
    if (store.ably.printers.indexOf(id) !== -1) {
      const printers = [...store.ably.printers];
      printers.splice(store.ably.printers.indexOf(id), 1);
      store.updateAbly({ printers });
    }
  };

  ablyRestaurantHandlePrinterJobUpdate = (message: Ably.Message) => {
    try {
      const { data } = message;

      const cacheKey = `printer:job-update:${data.job_id}`;
      const alreadyProcessed = ExpiryCache.get(cacheKey);
      if (!!alreadyProcessed) {
        return;
      } else {
        ExpiryCache.clearExpired();
        ExpiryCache.set(cacheKey, {}, 1000 * 60 * 60 * 2);
      }

      const order_number = data.order_number ? `${data.order_number} ` : '';
      if (data.error) {
        UI.notification.error(`Order ${order_number}failed to print`);
      } else {
        UI.notification.success(`Order ${order_number}printed`);
      }
    } catch (e) {
      logger.captureException(e);
    }
  };
  ablyRestaurantHandleOrderNew = (message: Ably.Message) => {
    if (this.store.restrictions._.restaurantOrderViews.length === 0) {
      return;
    }

    const { order_id } = message.data;

    // CHECK PROCESSED CACHE
    ExpiryCache.clearExpired();
    const cacheKey = `order:notification:created:${order_id}`;
    const alreadyProcessed = ExpiryCache.get(cacheKey);
    if (!!alreadyProcessed) {
      return;
    }
    ExpiryCache.set(cacheKey, {}, 1000 * 60 * 60 * 2);

    // PLAY AUDIO
    const an = this.anGet();
    if (an.play_on.order_new) {
      this.anPlay();
    }

    // SEND PN
    this.sendPushNotification('New Order Received', {
      timeout: 30000,
      requireInteraction: true,
    });

    // ADD TO BOARD OR LIST
    if (this.store.router.s.path.indexOf('/orders') !== -1) {
      this.store.service.order.handle_new(order_id);
    }
  };
  ablyRestaurantHandleOrderUpdate = (message: Ably.Message) => {
    if (this.store.restrictions._.restaurantOrderViews.length === 0) {
      return;
    }

    const { order_id } = message.data;

    // ADD TO BOARD OR LIST
    if (this.store.router.s.path.indexOf('/orders') !== -1) {
      this.store.service.order.handle_update(order_id);
    }
  };

  // Heads-up only: gift-card purchases are reviewed in Native Loyalty admin (Gift cards tab),
  // not here, so there's nothing in this dashboard to navigate to or refetch. Reuses the
  // "new order" sound toggle rather than adding a dedicated settings key for a single alert.
  // Heads-up only: gift-card purchases are reviewed in Native Loyalty admin (Gift cards tab)
  // or, for `pending_confirmation`, via the Notifications panel popup — nothing in this
  // dashboard to navigate to or refetch. Reuses the "new order" sound toggle rather than adding
  // a dedicated settings key for this alert.
  ablyRestaurantHandleGiftCardPurchaseCreated = (message: Ably.Message) => {
    const an = this.anGet();
    if (an.play_on.order_new) {
      this.anPlay();
    }

    const title =
      message.name === 'gift_card_purchase:pending_confirmation'
        ? 'Gift Card Purchase Awaiting Confirmation'
        : 'New Gift Card Purchase';
    this.sendPushNotification(title, {
      timeout: 30000,
      requireInteraction: true,
    });
  };

  ablyHandleOrderDishUpdate = async (message: Ably.Message) => {
    const { order_dish_status, order_dish_line_id, order_id } = message.data;
    // FORWARD TO FRONTEND
    if (order_dish_status) {
      const response = await this.store.api.order_find({ _id: order_id });
      if (response.outcome === 0) {
        this.store.updateOrder({ ...response.item });
        this.store.updateOrderComplete(response.item);
      }
    }
  };

  // GET STREAM
  start(api: string, token: string) {
    if (this.subscription) {
      this.subscription.cancel();
    }
    // DONT SUBSCRIBE IF NOTIFICATIONS NOT ENABLED
    if (!this.store.restrictions._.restaurantNotificationsEnabled) {
      return;
    }
    this.feed = client.feed('restaurant_notifications', api, token);
    this.subscription = this.feed.subscribe(this.handle_subscription);
    this.get({
      limit: 15,
      mark_seen: false,
      strategy: 'replace',
    });
  }
  @action stop() {
    if (this.subscription) {
      this.subscription.cancel();
    }
    this.s = baseNotificationState();
    delete this.feed;
    delete this.subscription;
  }
  @action open() {
    this.s.active = true;
    this.mark_seen();
  }
  @action close() {
    this.s.active = false;
  }
  @action on_scroll_bottom() {
    const { items, loading, all_items_queried } = this.s;
    if (!all_items_queried && !loading) {
      this.s.loading = true;
      const lastActivity = items[items.length - 1];
      if (lastActivity) {
        this.get({
          strategy: 'back',
          limit: 15,
          mark_seen: false,
          id_lt: lastActivity.group_id,
        });
      }
    }
  }

  @action async get(opts: {
    limit?: number;
    id_lt?: string;
    mark_seen: boolean;
    strategy: 'front' | 'back' | 'replace';
  }) {
    if (!this.feed) return;
    try {
      const { limit, id_lt, mark_seen, strategy } = opts;
      this.s.loading = true;

      const query: T.ObjectAny = {
        limit: limit || 15,
        mark_seen: mark_seen,
      };
      if (id_lt) query.id_lt = id_lt; // PAGINATE

      const res = await this.feed.get(query);
      const data = res as unknown as T.Lib.GetStream.StreamNotificationResponse;

      this.s.loading = false;
      this.s.unread = data.unread;
      this.s.unseen = mark_seen ? 0 : data.unseen;

      const newItems = data.results.map(item => ({
        ...item.activities[0],
        is_read: item.is_read,
        is_seen: item.is_seen,
        group_id: item.id,
      }));

      if (strategy === 'front') {
        this.s.items = newItems.concat(this.s.items);
      } else if (strategy === 'back') {
        this.s.items = this.s.items.concat(newItems);
      } else if (strategy === 'replace') {
        this.s.items = newItems;
      }

      // DON'T LOAD MORE THAN THE LAST 100 NOTIFICATIONS
      if (this.s.items.length > 100 || (id_lt && data.results.length === 0)) {
        this.s.all_items_queried = true;
      }
    } catch (e) {
      logger.captureException(e);
    }
  }
  @action async mark_seen() {
    if (!this.feed) return;
    const old = this.s.unseen;
    const oldItems = cloneDeep(this.s.items);
    try {
      this.s.unseen = 0;
      this.s.items = this.s.items.map(i => ({ ...i, is_seen: true }));
      await this.feed.get({ limit: 0, mark_seen: true });
    } catch (e) {
      logger.captureException(e);
      this.s.unseen = old;
      this.s.items = oldItems;
    }
  }
  @action async mark_read(id: string, index: number) {
    if (!this.feed) return;
    try {
      this.s.unread--;
      this.s.items[index].is_read = true;
      await this.feed.get({ limit: 0, mark_read: [id] });
    } catch (e) {
      logger.captureException(e);
      this.s.unread++;
      this.s.items[index].is_read = false;
    }
  }
  @action mark_read_object(type: 'customer' | 'order' | 'booking', id: string) {
    try {
      if (!this.feed) return;

      const mark_read = [];
      const mark_seen = [];

      for (let index = 0; index < this.s.items.length; index++) {
        const item = this.s.items[index];
        if ((!item.is_read || item.is_seen) && item.object) {
          const parts = (item.object as string)?.split('::') ?? [];
          if (type === parts[0] && id === parts[1]) {
            if (!item.is_read) {
              this.s.items[index].is_read = true;
              mark_read.push(item.group_id);
            }
            if (!item.is_seen) {
              this.s.items[index].is_seen = true;
              mark_seen.push(item.group_id);
            }
          }
        }
      }

      this.s.unread = this.s.unread - mark_read.length;
      this.s.unseen = this.s.unseen - mark_seen.length;
      this.feed.get({ limit: 0, mark_read: mark_read, mark_seen: mark_seen }).catch(logger.captureException);
    } catch (e) {
      logger.captureException(e);
    }
  }

  handle_subscription(res: any) {
    try {
      const data = res as T.Lib.GetStream.StreamSubscriptionResponse;
      let newNotifications = 0;
      // tslint:disable-next-line
      for (const activity of data.new.reverse()) {
        /*
				this.s.items.unshift({
				  ...activity,
				  is_read: false,
				  is_seen: false,
				} as T.Lib.GetStream.NotificationItem);
				// this.s.new++;
				// this.s.unread++;
				// this.s.unseen++;
				*/
        this.handle_activity(activity);
        newNotifications++;
      }
      this.get({
        limit: newNotifications,
        mark_seen: this.s.active, // IF DROPDOWN ACTIVE MARK IT AS SEEN
        strategy: 'front',
      });
    } catch (e) {
      logger.captureException(e);
    }
  }
  handle_activity(activity: T.Lib.GetStream.StreamActivityItem) {
    const rr = this.store.restrictions.restaurant;
    const an = this.anGet();
    if (activity.verb === 'age_verification' && rr.customers) {
      if (an.play_on.customer_age_verification) {
        this.anPlay();
      }
    } else if (activity.verb === 'booking_new' && rr.bookings) {
      const booking_id = (activity.object as string)?.split('::')[1] ?? '';
      const page = this.store.bookings.page;
      if (an.play_on.booking_new) {
        this.anPlay();
      }
      this.sendPushNotification('New Booking Received', {
        timeout: 30000,
        requireInteraction: true,
      });
      if (page === 1) {
        this.store.service.booking.handle_new_booking(booking_id);
      }
    } else if (activity.verb === 'booking_cancelled' && rr.bookings) {
      const booking_id = (activity.object as string)?.split('::')[1] ?? '';
      if (an.play_on.booking_new) {
        this.anPlay();
      }
      this.sendPushNotification('Booking Cancelled', {
        timeout: 30000,
        requireInteraction: true,
      });
      void this.store.service.booking.handle_booking_refresh(booking_id);
    }
  }
  @action handle_click(activity: T.Lib.GetStream.NotificationItem, i: number) {
    const { router, restaurant } = this.store;
    const { verb, object, is_read, group_id } = activity;

    this.s.active = false;

    if (!is_read) {
      this.mark_read(group_id, i);
    }

    if (verb === 'age_verification') {
      const customer_id = (object as string)?.split('::')[1] ?? '';
      router.push(`/restaurant/${restaurant!._id}/customers?_id=${customer_id}`);
    } else if (verb === 'order_new' || verb === 'fraud_risk_order_new') {
      const order_id = (object as string)?.split('::')[1] ?? '';
      router.push(`/restaurant/${restaurant!._id}/orders?_id=${order_id}`);
    } else if (verb === 'uber_order_cancel') {
      const order_id = (object as string)?.split('::')[1] ?? '';
      router.push(`/restaurant/${restaurant!._id}/orders?_id=${order_id}`);
    } else if (verb === 'booking_new' || verb === 'booking_cancelled') {
      const booking_id = (object as string)?.split('::')[1] ?? '';
      router.push(`/restaurant/${restaurant!._id}/bookings?_id=${booking_id}`);
    } else if (verb === 'order_deleted') {
      router.push(`/restaurant/${restaurant!._id}/orders`);
    } else if (verb === 'booking_deleted') {
      router.push(`/restaurant/${restaurant!._id}/bookings`);
    } else if (
      verb === 'gift_card_purchase_pending_confirmation' ||
      verb === 'gift_card_purchase_confirmed'
    ) {
      // Open the purchase detail side drawer for both gift card purchase events.
      this.update({ modal_item: activity });
    }
  }

  @action close_modal() {
    this.update({ modal_item: null });
  }

  // AUDIO NOTIFICATIONS
  anInit = () => {
    document.body.addEventListener('touchstart', this.anInitCallback, false);
    document.body.addEventListener('mousedown', this.anInitCallback, false);

    // CHECK AUDIO CONTEXT STATUS EVERY 3 SECONDS AND UPDATE ACCORDINGLY
    setInterval(() => {
      if (!Howler.ctx || Howler.ctx.state !== 'running') {
        this.update({ audio_unlocked: false });
      } else {
        this.update({ audio_unlocked: true });
      }
    }, 3000);
  };
  anInitCallback = (e: MouseEvent | TouchEvent) => {
    setTimeout(() => {
      if (Howler.ctx && Howler.ctx.state === 'running') {
        this.update({ audio_unlocked: true });
        document.body.removeEventListener('touchstart', this.anInitCallback, false);
        document.body.removeEventListener('mousedown', this.anInitCallback, false);
      }
    }, 300);
  };
  anGet = () => {
    const defaultSettings = RestaurantUtils.settings.defaultAudioNotifications();
    if (!this.store.restaurant) {
      return defaultSettings;
    }
    return this.store.restaurant.settings.notifications.audio || defaultSettings;
  };
  anPlay = async () => {
    try {
      if (this.soundPlaying) return;
      const an = this.anGet();

      const s = !an.sound
        ? null
        : new Howl({
            src: [`${config.urls.cloudfront}/assets/audio/${an.sound.split('.')[0] + '.mp3'}`],
            volume: 1,
            loop: true,
          });

      if (!s) return;

      let playCount = 0;
      s.on('end', () => {
        playCount++;
        if (playCount >= an.repeat_count) {
          s.stop();
          this.soundPlaying = false;
        }
      });

      s.play();

      this.soundPlaying = true;

      const stopSoundOnClick = () => {
        if (s && s.playing()) s.stop();
        this.soundPlaying = false;
        document.body.removeEventListener('mousedown', stopSoundOnClick);
        document.body.removeEventListener('touchstart', stopSoundOnClick);
      };

      document.body.addEventListener('mousedown', stopSoundOnClick);
      document.body.addEventListener('touchstart', stopSoundOnClick);
    } catch (e) {
      logger.captureException(e);
    }
  };

  // PUSH NOTIFICATIONS
  initPushNotification = () => {
    try {
      if (!Push.Permission.has() && !this.pushNotificationsInitialized) {
        const onGranted = () => console.log('Push notification permission granted');
        const onDenied = () => console.log('Push notification permission denied');
        Push.Permission.request(onGranted, onDenied);
        this.pushNotificationsInitialized = true;
      }
    } catch (e) {
      logger.captureException(e);
    }
  };
  sendPushNotification = async (title: string, opts: PushNotificationParams) => {
    try {
      if (Push.Permission.has()) {
        const n = await Push.create(title, {
          vibrate: true,
          icon: '/store-notification-icon.png',
          link: this.store.router.s.path,
          onClick: () => {
            // @ts-ignore
            try {
              n.close();
              window.focus();
            } catch (e) {
              logger.captureException(e);
            }
          },
          ...opts,
        });
      }
    } catch (e) {
      logger.captureException(e);
    }
  };

  @action set = (data: NotificationsState) => {
    this.s = data;
  };
  @action update = (data: Partial<NotificationsState>) => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key as keyof NotificationsState];
        if (value !== undefined) {
          // @ts-ignore
          this.s[key as keyof NotificationsState] = value;
        }
      }
    }
  };

  clearMenuEditingTimers = () => {
    if (this.menuEditingClaimTimer) {
      clearTimeout(this.menuEditingClaimTimer);
      delete this.menuEditingClaimTimer;
    }
    if (this.menuEditStalenessInterval) {
      clearInterval(this.menuEditStalenessInterval);
      delete this.menuEditStalenessInterval;
    }
  };

  ablyMenuEditLockMessage = (message: Ably.Message) => {
    try {
      const data = message.data as {
        action?: string;
        connectionId?: string;
        userId?: string;
        userName?: string;
        ts?: number;
      };
      if (!data || typeof data !== 'object') {
        return;
      }
      if (data.action === 'claim' || data.action === 'takeover') {
        const { connectionId, userId, ts, userName } = data;
        if (!connectionId || !userId || typeof ts !== 'number') {
          return;
        }
        const cur = this.store.ably.menuEditLock;
        if (!cur || ts >= cur.ts) {
          this.store.updateAbly({
            menuEditLock: {
              connectionId,
              userId,
              userName: userName || '',
              ts,
            },
          });
        }
      } else if (data.action === 'release') {
        const { connectionId } = data;
        const cur = this.store.ably.menuEditLock;
        if (cur && connectionId === cur.connectionId) {
          this.store.updateAbly({ menuEditLock: null });
          if (this.menuEditingPageRefCount > 0) {
            this.scheduleMenuEditingClaim();
          }
        }
      }
    } catch (e) {
      logger.captureException(e);
    }
  };

  maybePublishMenuEditReleaseOnDisconnect = () => {
    const lock = this.store.ably.menuEditLock;
    const ably = this.ablyRestaurant;
    if (!lock || !ably) {
      return;
    }
    const cid = ably.connection.id;
    if (cid && lock.connectionId === cid) {
      this.publishMenuEditRelease(lock.connectionId);
    }
  };

  publishMenuEditRelease = (connectionId?: string) => {
    const ably = this.ablyRestaurant;
    const rid = this.ablyRestaurantRid || this.store.restaurant?._id;
    if (!ably || !rid) {
      return;
    }
    const cid = connectionId || ably.connection.id;
    if (!cid) {
      return;
    }
    try {
      const ch = ably.channels.get(`private:restaurant:${rid}`);
      ch.publish('menu-v2:edit-lock', { action: 'release', connectionId: cid });
    } catch (e) {
      logger.captureException(e);
    }
  };

  publishMenuEditLockAction = (action: 'claim' | 'takeover') => {
    const ably = this.ablyRestaurant;
    const rid = this.ablyRestaurantRid || this.store.restaurant?._id;
    const auth = this.store.auth;
    if (!ably || ably.connection.state !== 'connected' || !rid || auth.type === null) {
      return;
    }
    const connectionId = ably.connection.id;
    if (!connectionId) {
      return;
    }
    const userId = auth.item?._id;
    if (!userId) {
      return;
    }
    const userName =
      (auth.item as { name?: string; email?: string })?.name ||
      (auth.item as { name?: string; email?: string })?.email ||
      '';
    const ts = Date.now();
    try {
      const ch = ably.channels.get(`private:restaurant:${rid}`);
      ch.publish('menu-v2:edit-lock', {
        action,
        connectionId,
        userId,
        userName,
        ts,
      });
    } catch (e) {
      logger.captureException(e);
    }
  };

  scheduleMenuEditingClaim = () => {
    if (this.menuEditingClaimTimer) {
      clearTimeout(this.menuEditingClaimTimer);
    }
    this.menuEditingClaimTimer = setTimeout(() => {
      delete this.menuEditingClaimTimer;
      this.publishMenuEditLockAction('claim');
    }, 450);
  };

  menuEditingMenusMounted = () => {
    this.menuEditingPageRefCount++;
    this.scheduleMenuEditingClaim();
    void this.menuEditingPresenceEnter();
  };

  menuEditingPresenceEnterIfOnMenusPage = () => {
    if (this.menuEditingPageRefCount > 0) {
      void this.menuEditingPresenceEnter();
    }
  };

  menuEditingMenusUnmounted = () => {
    void this.menuEditingPresenceLeave();
    const lock = this.store.ably.menuEditLock;
    const ably = this.ablyRestaurant;
    const cid = ably?.connection?.id;
    if (lock && cid && lock.connectionId === cid) {
      this.publishMenuEditRelease(lock.connectionId);
    }
    this.menuEditingPageRefCount = Math.max(0, this.menuEditingPageRefCount - 1);
    if (this.menuEditingClaimTimer) {
      clearTimeout(this.menuEditingClaimTimer);
      delete this.menuEditingClaimTimer;
    }
  };

  menuEditIsCurrentHolder = (): boolean => {
    const ably = this.ablyRestaurant;
    const lock = this.store.ably.menuEditLock;
    if (!ably || !lock || ably.connection.state !== 'connected') {
      return false;
    }
    const cid = ably.connection.id;
    return !!cid && lock.connectionId === cid;
  };

  menuEditingRequestTakeover = () => {
    this.publishMenuEditLockAction('takeover');
  };

  menuEditingPresenceEnter = async () => {
    try {
      const ably = this.ablyRestaurant;
      const rid = this.ablyRestaurantRid || this.store.restaurant?._id;
      if (!ably || !rid || ably.connection.state !== 'connected') {
        return;
      }
      const ch = ably.channels.get(`private:restaurant:${rid}`);
      await ch.presence.enter(this.menuPresenceData());
      this.scheduleMenuPresenceRefresh();
    } catch (e) {
      logger.captureException(e);
    }
  };

  /** menuDashboard is what the edit lock looks for; `type` is left unset so the printer roster ignores us. */
  menuPresenceData = () => {
    const auth = this.store.auth;
    const item = auth.item as { name?: string; email?: string } | undefined;
    return {
      menuDashboard: true,
      menu: {
        userId: item ? auth.item?._id : '',
        name: item?.name || item?.email || 'Someone',
        menuId: this.menuPresenceMenuId,
        entityType: this.menuPresenceEntityType,
        entityId: this.menuPresenceEntityId,
        entityName: this.menuPresenceEntityName,
        activity: this.menuPresenceActivity,
        since: this.menuPresenceSince || Date.now(),
      },
    };
  };

  /** Reference counted: the list and an editor can both be mounted, and the first to unmount must not drop the roster. */
  menuPresenceMounted = (menuId: string | null, activity: 'viewing' | 'editing' = 'viewing') => {
    this.menuPresenceRefCount++;
    this.menuPresenceMenuId = menuId;
    this.menuPresenceActivity = activity;
    this.menuPresenceSince = Date.now();
    void this.menuPresencePublish();
  };

  menuPresenceUnmounted = (menuId: string | null) => {
    this.menuPresenceRefCount = Math.max(0, this.menuPresenceRefCount - 1);

    if (this.menuPresenceRefCount === 0) {
      this.menuPresenceMenuId = null;
      this.menuPresenceClearEntityFields();
      void this.menuPresenceLeave();
      return;
    }

    // A menu editor closed but the list underneath is still open, so fall back
    // to viewing the list rather than leaving presence entirely.
    if (menuId && this.menuPresenceMenuId === menuId) {
      this.menuPresenceMenuId = null;
      this.menuPresenceClearEntityFields();
      void this.menuPresencePublish();
    }
  };

  menuPresenceSetMenu = (menuId: string | null, activity: 'viewing' | 'editing' = 'viewing') => {
    if (this.menuPresenceMenuId === menuId && this.menuPresenceActivity === activity) {
      return;
    }
    this.menuPresenceMenuId = menuId;
    this.menuPresenceActivity = activity;
    this.menuPresenceSince = Date.now();
    void this.menuPresencePublish();
  };

  /** No menu id: the list already recorded it, and an editor would have to walk indices back to repeat it. */
  menuPresenceSetEntity = (
    entityType: MenuPresenceEntityType,
    entityId: string | null,
    entityName: string | null
  ) => {
    this.menuPresenceEntityType = entityType;
    this.menuPresenceEntityId = entityId;
    this.menuPresenceEntityName = entityName;
    this.menuPresenceActivity = 'editing';
    this.menuPresenceSince = Date.now();
    void this.menuPresencePublish();
  };

  menuPresenceClearEntity = () => {
    if (!this.menuPresenceEntityType) {
      return;
    }
    this.menuPresenceClearEntityFields();
    void this.menuPresencePublish();
  };

  menuPresenceClearEntityFields = () => {
    this.menuPresenceEntityType = null;
    this.menuPresenceEntityId = null;
    this.menuPresenceEntityName = null;
    this.menuPresenceActivity = 'viewing';
  };

  /** Update first, enter on failure: Ably rejects update before enter, and enter after it replaces the data. */
  menuPresencePublish = async () => {
    try {
      const ably = this.ablyRestaurant;
      const rid = this.ablyRestaurantRid || this.store.restaurant?._id;
      if (!ably || !rid || ably.connection.state !== 'connected' || this.menuPresenceRefCount === 0) {
        return;
      }
      const ch = ably.channels.get(`private:restaurant:${rid}`);
      await ch.presence.update(this.menuPresenceData());
      this.scheduleMenuPresenceRefresh();
    } catch (e) {
      try {
        await this.menuEditingPresenceEnter();
      } catch (inner) {
        logger.captureException(inner);
      }
    }
  };

  menuPresenceLeave = async () => {
    this.store.updateAbly({ menuPresence: [] });
    try {
      const ably = this.ablyRestaurant;
      const rid = this.ablyRestaurantRid || this.store.restaurant?._id;
      if (!ably || !rid) {
        return;
      }
      const ch = ably.channels.get(`private:restaurant:${rid}`);
      await ch.presence.leave();
    } catch (e) {
      /* connection may already be closed */
    }
  };

  /** Rebuilt from the whole set, not incrementally: a missed event otherwise leaves a ghost on screen. */
  scheduleMenuPresenceRefresh = () => {
    if (this.menuPresenceSyncTimer) {
      clearTimeout(this.menuPresenceSyncTimer);
    }
    this.menuPresenceSyncTimer = setTimeout(() => {
      delete this.menuPresenceSyncTimer;
      void this.menuPresenceRefresh();
    }, 250);
  };

  menuPresenceRefresh = async () => {
    try {
      const ably = this.ablyRestaurant;
      const rid = this.ablyRestaurantRid || this.store.restaurant?._id;
      if (!ably || !rid) {
        return;
      }
      if (this.menuPresenceRefCount === 0) {
        this.store.updateAbly({ menuPresence: [] });
        return;
      }

      const ch = ably.channels.get(`private:restaurant:${rid}`);
      const members = await ch.presence.get();
      const myConnectionId = ably.connection.id;

      const roster: MenuPresenceMember[] = [];
      for (const member of members) {
        const data = member.data as { menu?: Partial<MenuPresenceMember> } | undefined;
        if (!data || !data.menu) {
          continue;
        }
        if (member.connectionId === myConnectionId) {
          continue;
        }
        roster.push({
          connectionId: member.connectionId,
          userId: data.menu.userId || member.clientId || '',
          name: data.menu.name || 'Someone',
          menuId: data.menu.menuId ?? null,
          entityType: data.menu.entityType ?? null,
          entityId: data.menu.entityId ?? null,
          entityName: data.menu.entityName ?? null,
          activity: data.menu.activity === 'editing' ? 'editing' : 'viewing',
          since: data.menu.since || Date.now(),
        });
      }

      roster.sort((a, b) => a.since - b.since);
      this.store.updateAbly({ menuPresence: roster });
    } catch (e) {
      /* presence is advisory; never surface a failure to read it */
    }
  };

  menuEditingPresenceLeave = async () => {
    try {
      const ably = this.ablyRestaurant;
      const rid = this.ablyRestaurantRid || this.store.restaurant?._id;
      if (!ably || !rid) {
        return;
      }
      const ch = ably.channels.get(`private:restaurant:${rid}`);
      await ch.presence.leave();
    } catch (e) {
      /* connection may already be closed */
    }
  };

  startMenuEditStalenessCheck = (channel: ReturnType<Realtime['channels']['get']>) => {
    if (this.menuEditStalenessInterval) {
      clearInterval(this.menuEditStalenessInterval);
    }
    this.menuEditStalenessInterval = setInterval(async () => {
      const lock = this.store.ably.menuEditLock;
      if (!lock) {
        return;
      }
      try {
        const members = await channel.presence.get();
        const present = members.some(m => m.connectionId === lock.connectionId);
        if (!present) {
          this.store.updateAbly({ menuEditLock: null });
          if (this.menuEditingPageRefCount > 0) {
            this.scheduleMenuEditingClaim();
          }
        }
      } catch (e) {
        /* ignore */
      }
    }, 45000);
  };

}
